import { useEffect, useRef } from "react";
import { Shield } from "lucide-react";
import { HITOS, diasActivosLocal } from "../../lib/rachaDiaria";

// Hoja "Tu racha": el detalle sobrio detrás de la tarjeta del Home. Nada de
// celebración adicional acá — solo los datos (racha, máxima, días totales),
// los escudos explicados en una línea humana, el camino de hitos y (si hay
// datos locales) un mini-mapa del mes en curso. Mismo patrón de bottom sheet
// que DetalleDiaSheet de calendario (overlay + panel + drag to dismiss).

const DIAS_SEMANA = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function addDays(d, n) { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }

// Clave 'YYYY-MM-DD' en hora LOCAL (igual que fechaKey en turnoRacha.js).
function keyLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Mini-mapa del mes en curso: grilla lunes-domingo con un punto en los días
// activos. Solo se llama si hay al menos un día local guardado.
function MiniMapaMes({ diasActivos }) {
  const hoy    = new Date();
  const activos = new Set(diasActivos);
  const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  let dow = primerDia.getDay();
  if (dow === 0) dow = 7; // domingo → 7, para que la semana empiece lunes
  const inicio = addDays(primerDia, -(dow - 1));
  const celdas = Array.from({ length: 42 }, (_, i) => addDays(inicio, i));
  const ultimaConDatos = Math.max(
    ...celdas.map((d, i) => (d.getMonth() === hoy.getMonth() ? i : -1))
  );

  return (
    <div className="mt-2">
      <div className="grid grid-cols-7 gap-1">
        {DIAS_SEMANA.map((d, i) => (
          <div key={i} className="text-center text-micro text-ink-faint font-sans">{d}</div>
        ))}
        {celdas.slice(0, ultimaConDatos + 1).map((dia, i) => {
          const esMesActual = dia.getMonth() === hoy.getMonth();
          if (!esMesActual) return <div key={i} />;
          const esHoy   = keyLocal(dia) === keyLocal(hoy);
          const activo  = activos.has(keyLocal(dia));
          return (
            <div key={i} className="flex flex-col items-center gap-0.5 py-0.5">
              <span className={`text-micro font-sans tabular-nums ${esHoy ? 'text-ink font-semibold' : 'text-ink-faint'}`}>
                {dia.getDate()}
              </span>
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${activo ? 'bg-accent' : 'bg-transparent'}`}
                aria-hidden="true"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Props: abierto · onCerrar · racha · max · dias (días activos totales) ·
// escudos · userId (para leer el mapa local del mes).
export default function RachaSheet({ abierto, onCerrar, racha = 0, max = 0, dias = 0, escudos = 0, userId }) {
  const touchStartY = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    const fn = (e) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [abierto, onCerrar]);

  useEffect(() => {
    document.body.style.overflow = abierto ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [abierto]);

  if (!abierto) return null;

  const diasActivos = diasActivosLocal(userId);
  const hayMapa      = diasActivos.length > 0;

  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd   = (e) => {
    if (touchStartY.current === null) return;
    if (e.changedTouches[0].clientY - touchStartY.current > 60) onCerrar();
    touchStartY.current = null;
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onCerrar}
        aria-hidden="true"
        style={{ animation: 'fadeInOverlayRacha 250ms ease-out' }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Tu racha"
        className="fixed inset-x-0 bottom-0 z-50 bg-paper rounded-t-[20px] max-h-[85vh] overflow-y-auto"
        style={{ animation: 'slideUpRacha 250ms ease-out', boxShadow: '0 -4px 32px rgba(0,0,0,0.12)' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-9 h-1 rounded-full bg-edge" />
        </div>

        <div className="px-5 pt-3 pb-8">
          <h2 className="font-serif text-20px text-ink mb-4">Tu racha</h2>

          {/* (a) racha actual + máxima + días activos totales */}
          <div className="flex items-baseline gap-6 mb-5">
            <div>
              <span className="font-serif text-32px leading-none tabular-nums text-ink">{racha}</span>
              <p className="text-small text-ink-muted font-sans mt-1">{racha === 1 ? 'día seguido' : 'días seguidos'}</p>
            </div>
            <div>
              <span className="font-serif text-22px leading-none tabular-nums text-ink-subtle">{max}</span>
              <p className="text-small text-ink-muted font-sans mt-1">racha máxima</p>
            </div>
            <div>
              <span className="font-serif text-22px leading-none tabular-nums text-ink-subtle">{dias}</span>
              <p className="text-small text-ink-muted font-sans mt-1">{dias === 1 ? 'día activo' : 'días activos'}</p>
            </div>
          </div>

          {/* (b) escudos, en una línea humana */}
          <div className="flex items-start gap-2 mb-5 rounded-tk-md bg-surface p-3">
            <Shield size={16} strokeWidth={1.8} className="text-accent flex-shrink-0 mt-0.5" />
            <p className="text-small text-ink font-sans leading-snug">
              Tienes {escudos} de 2 · ganas 1 cada 7 días seguidos · protegen un día que faltes.
              Y tus días libres del calendario no rompen nada: descansar también es parte del turno.
            </p>
          </div>

          {/* (c) camino de hitos, marcando los alcanzados según la racha máxima */}
          <div className="mb-5">
            <p className="text-micro text-ink-faint font-sans mb-2 uppercase tracking-wide">Camino</p>
            <div className="flex items-center gap-1.5">
              {HITOS.map((h, i) => {
                const alcanzado = max >= h;
                return (
                  <div key={h} className="flex items-center gap-1.5 flex-1">
                    <div
                      className={`grid place-items-center w-8 h-8 rounded-full flex-shrink-0 text-11px font-sans font-semibold tabular-nums ${
                        alcanzado ? 'bg-accent text-white' : 'bg-surface text-ink-faint border border-edge'
                      }`}
                      title={`${h} días`}
                    >
                      {h}
                    </div>
                    {i < HITOS.length - 1 && (
                      <div className={`h-px flex-1 ${alcanzado && max >= HITOS[i + 1] ? 'bg-accent' : 'bg-edge'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* (d) mini-mapa del mes en curso, solo si hay datos locales */}
          {hayMapa && (
            <div>
              <p className="text-micro text-ink-faint font-sans mb-2 uppercase tracking-wide">Este mes</p>
              <MiniMapaMes diasActivos={diasActivos} />
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUpRacha {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes fadeInOverlayRacha {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </>
  );
}
